import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useTriggerLoad } from "@redotech/react-util/load";
import AlertTriangleIcon from "@redotech/redo-icons/alert-triangle.svg";
import PackageIcon from "@redotech/redo-icons/package.svg";
import XClose from "@redotech/redo-icons/x-close.svg";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { ImageWithPlaceholder } from "@redotech/redo-web/image-with-placeholder";
import { Text } from "@redotech/redo-web/text";
import { memo, useEffect, useMemo, useState } from "react";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";
import { LotBadge } from "../wms/shared/lot-badge";
import {
  applyBinToAllProducts,
  buildApplyAllOptions,
  buildBinSelections,
  computeDefaultAllocations,
  type BinSelections,
  type ProductBinInfo,
} from "./bin-selection";
import * as styles from "./bin-selection-modal.module.css";

interface BinSelectionModalProps {
  open: boolean;
  lineItems: ProductBinInfo[];
  fulfillmentGroupIds: string[];
  onConfirm: (selections: BinSelections) => void;
  onCancel: () => void;
  onBypass?: () => void;
  bypassLabel?: string;
  confirmLabel?: string;
  recordOnConfirm?: boolean;
}

interface InventoryAdjustmentWarningModalProps {
  open: boolean;
  lineItems: ProductBinInfo[];
  onConfirm: () => void;
  onCancel: () => void;
  onBypass?: () => void;
  bypassLabel?: string;
  confirmLabel?: string;
}

export interface OpenBinSelectionModalParams {
  lineItems: ProductBinInfo[];
  fulfillmentGroupIds: string[];
  onConfirm: (selections: BinSelections) => void;
  onCancel?: () => void;
  onBypass?: () => void;
  bypassLabel?: string;
  confirmLabel?: string;
  recordOnConfirm?: boolean;
}

export interface OpenInventoryAdjustmentWarningModalParams {
  lineItems: ProductBinInfo[];
  onConfirm: () => void;
  onCancel?: () => void;
  onBypass?: () => void;
  bypassLabel?: string;
  confirmLabel?: string;
}

export function useBinSelectionModal() {
  const [state, setState] = useState<{
    open: boolean;
    lineItems: ProductBinInfo[];
    fulfillmentGroupIds: string[];
    onConfirm: (selections: BinSelections) => void;
    onCancel?: () => void;
    onBypass?: () => void;
    bypassLabel?: string;
    confirmLabel?: string;
    recordOnConfirm?: boolean;
    mode: "bin-selection" | "inventory-adjustment-warning";
  }>({
    open: false,
    lineItems: [],
    fulfillmentGroupIds: [],
    onConfirm: () => {},
    mode: "bin-selection",
  });

  const openBinSelectionModal = useHandler(
    (params: OpenBinSelectionModalParams) => {
      setState({ open: true, mode: "bin-selection", ...params });
    },
  );

  const openInventoryAdjustmentWarningModal = useHandler(
    (params: OpenInventoryAdjustmentWarningModalParams) => {
      setState({
        open: true,
        lineItems: params.lineItems,
        fulfillmentGroupIds: [],
        onConfirm: params.onConfirm,
        onCancel: params.onCancel,
        onBypass: params.onBypass,
        bypassLabel: params.bypassLabel,
        confirmLabel: params.confirmLabel,
        recordOnConfirm: undefined,
        mode: "inventory-adjustment-warning",
      });
    },
  );

  const modalProps: BinSelectionModalProps = useMemo(
    () => ({
      open: state.open && state.mode === "bin-selection",
      lineItems: state.lineItems,
      fulfillmentGroupIds: state.fulfillmentGroupIds,
      recordOnConfirm: state.recordOnConfirm,
      onConfirm: (selections: BinSelections) => {
        setState((s) => ({ ...s, open: false }));
        state.onConfirm(selections);
      },
      onCancel: () => {
        setState((s) => ({ ...s, open: false }));
        state.onCancel?.();
      },
      onBypass: state.onBypass
        ? () => {
            setState((s) => ({ ...s, open: false }));
            state.onBypass?.();
          }
        : undefined,
      bypassLabel: state.bypassLabel,
      confirmLabel: state.confirmLabel,
    }),
    [state],
  );

  const inventoryAdjustmentWarningModalProps: InventoryAdjustmentWarningModalProps =
    useMemo(
      () => ({
        open: state.open && state.mode === "inventory-adjustment-warning",
        lineItems: state.lineItems,
        onConfirm: () => {
          setState((s) => ({ ...s, open: false }));
          state.onConfirm({});
        },
        onCancel: () => {
          setState((s) => ({ ...s, open: false }));
          state.onCancel?.();
        },
        onBypass: state.onBypass
          ? () => {
              setState((s) => ({ ...s, open: false }));
              state.onBypass?.();
            }
          : undefined,
        bypassLabel: state.bypassLabel,
        confirmLabel: state.confirmLabel,
      }),
      [state],
    );

  return {
    modalProps,
    inventoryAdjustmentWarningModalProps,
    openBinSelectionModal,
    openInventoryAdjustmentWarningModal,
  };
}

export const BinSelectionModal = memo(function BinSelectionModal({
  open,
  lineItems,
  fulfillmentGroupIds,
  onConfirm,
  onCancel,
  onBypass,
  bypassLabel = "Bypass",
  confirmLabel = "Confirm and buy label",
  recordOnConfirm = true,
}: BinSelectionModalProps) {
  const client = useRequiredContext(RedoOutboundLabelsRpcClientContext);

  // Map<productId, Map<storageLocationId, quantity>>
  const [allocations, setAllocations] = useState<
    Map<string, Map<string, number>>
  >(new Map());

  // Track which bins are visible per product (initialized from default allocations)
  const [visibleBins, setVisibleBins] = useState<Map<string, Set<string>>>(
    new Map(),
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    const initialAllocations = new Map<string, Map<string, number>>();
    const initialVisible = new Map<string, Set<string>>();
    for (const item of lineItems) {
      const defaults = computeDefaultAllocations(item);
      initialAllocations.set(item.productId, defaults);
      initialVisible.set(item.productId, new Set(defaults.keys()));
    }
    setAllocations(initialAllocations);
    setVisibleBins(initialVisible);
  }, [open, lineItems]);

  const updateAllocation = (
    productId: string,
    storageLocationId: string,
    quantity: number,
  ) => {
    setAllocations((prev) => {
      const next = new Map(prev);
      const productAllocs = new Map(next.get(productId) ?? new Map());
      if (quantity <= 0) {
        productAllocs.delete(storageLocationId);
      } else {
        productAllocs.set(storageLocationId, quantity);
      }
      next.set(productId, productAllocs);
      return next;
    });
  };

  const getProductTotal = (productId: string): number => {
    const productAllocs = allocations.get(productId);
    if (!productAllocs) {
      return 0;
    }
    let total = 0;
    for (const qty of productAllocs.values()) {
      total += qty;
    }
    return total;
  };

  const addBin = useHandler((productId: string, storageLocationId: string) => {
    setVisibleBins((prev) => {
      const next = new Map(prev);
      const bins = new Set(next.get(productId));
      bins.add(storageLocationId);
      next.set(productId, bins);
      return next;
    });
  });

  const removeBin = useHandler(
    (productId: string, storageLocationId: string) => {
      setVisibleBins((prev) => {
        const next = new Map(prev);
        const bins = new Set(next.get(productId));
        bins.delete(storageLocationId);
        next.set(productId, bins);
        return next;
      });
      updateAllocation(productId, storageLocationId, 0);
    },
  );

  const [applyAllBinId, setApplyAllBinId] = useState<string | null>(null);
  const [applyAllSummary, setApplyAllSummary] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setApplyAllBinId(null);
      setApplyAllSummary(null);
    }
  }, [open]);

  const applyAllOptions = useMemo(
    () => buildApplyAllOptions(lineItems),
    [lineItems],
  );

  const applyToAll = useHandler(() => {
    if (!applyAllBinId) {
      return;
    }
    const result = applyBinToAllProducts(
      lineItems,
      allocations,
      visibleBins,
      applyAllBinId,
    );
    setAllocations(result.allocations);
    setVisibleBins(result.visibleBins);
    setApplyAllSummary(
      `Applied to ${result.appliedProductIds.length} of ${lineItems.length} products. Products without enough stock in that bin were left unchanged.`,
    );
  });

  const isValid = lineItems.every(
    (item) => getProductTotal(item.productId) === item.requiredQuantity,
  );

  const [submitLoad, triggerSubmit] = useTriggerLoad(async () => {
    const binSelections = buildBinSelections(lineItems, allocations);
    if (!recordOnConfirm) {
      onConfirm(binSelections);
      return;
    }
    try {
      await client.recordDirectBinPicks({ fulfillmentGroupIds, binSelections });
      onConfirm(binSelections);
    } catch (error) {
      toast("Failed to record bin selection", { variant: "error" });
    }
  });

  if (lineItems.length === 0) {
    return null;
  }

  return (
    <RedoModal
      hideFooter
      isOpen={open}
      modalSize="md"
      onModalCloseRequested={onCancel}
      subtitle={`Select which bins to pull items from for ${
        fulfillmentGroupIds.length === 1
          ? "this fulfillment"
          : "these fulfillments"
      }`}
      theme="brand"
      title="Select bin locations"
    >
      <Flex dir="column" gap="xl" pb="3xl">
        {applyAllOptions.length > 0 && (
          <Flex dir="column" gap="xs">
            <Flex align="center" dir="row" gap="md">
              <Flex grow={1}>
                <RedoSingleSelectDropdownInput
                  options={applyAllOptions}
                  optionSelected={({ item: selected }) => {
                    setApplyAllBinId(selected.value);
                    setApplyAllSummary(null);
                  }}
                  placeholder="Pull every product from one bin..."
                  selectedItem={applyAllOptions.find(
                    (option) => option.value === applyAllBinId,
                  )}
                  size="xs"
                />
              </Flex>
              <RedoButton
                disabled={!applyAllBinId}
                hierarchy="secondary"
                onClick={applyToAll}
                size="sm"
                text="Apply to all"
                theme="normal"
              />
            </Flex>
            {applyAllSummary && (
              <Text fontSize="xs" textColor="tertiary">
                {applyAllSummary}
              </Text>
            )}
          </Flex>
        )}
        {lineItems.map((item) => {
          const productTotal = getProductTotal(item.productId);
          const productAllocs = allocations.get(item.productId) ?? new Map();
          const productVisible =
            visibleBins.get(item.productId) ?? new Set<string>();

          return (
            <Flex dir="column" gap="md" key={item.productId}>
              <Flex align="center" dir="row" gap="md" justify="space-between">
                <Flex align="center" dir="row" gap="md">
                  <ImageWithPlaceholder
                    className="shrink-0 overflow-hidden rounded-md"
                    DefaultIcon={PackageIcon}
                    height={32}
                    image={{
                      url: item.imageUrl ?? null,
                      altText: item.productName,
                      thumbhash: null,
                    }}
                    width={32}
                  />
                  <Text fontWeight="semibold">{item.productName}</Text>
                </Flex>
                <Text
                  fontSize="xs"
                  textColor={
                    productTotal === item.requiredQuantity ? "primary" : "error"
                  }
                >
                  {productTotal} / {item.requiredQuantity}
                </Text>
              </Flex>

              {item.bins.length === 0 ? (
                <Text fontSize="xs" textColor="secondary">
                  No bins available for this product
                </Text>
              ) : (
                <>
                  {item.bins
                    .filter((bin) => productVisible.has(bin.storageLocationId))
                    .map((bin) => (
                      <Flex
                        align="center"
                        dir="row"
                        gap="md"
                        key={bin.storageLocationId}
                      >
                        <Flex dir="column" grow={1}>
                          <Flex align="center" dir="row" gap="xs">
                            <Text fontSize="sm">{bin.binName}</Text>
                            {bin.lotNumber && (
                              <LotBadge
                                expirationDate={bin.expirationDate}
                                lotNumber={bin.lotNumber}
                              />
                            )}
                          </Flex>
                          <Text fontSize="xxs" textColor="tertiary">
                            Available: {bin.availableQuantity}
                          </Text>
                        </Flex>
                        <Flex className={styles.quantityInput}>
                          <RedoTextInput
                            inputTypeHint="number"
                            max={bin.availableQuantity}
                            min={0}
                            setValue={(val) => {
                              const num = parseInt(val, 10);
                              updateAllocation(
                                item.productId,
                                bin.storageLocationId,
                                isNaN(num)
                                  ? 0
                                  : Math.min(num, bin.availableQuantity),
                              );
                            }}
                            size="sm"
                            value={String(
                              productAllocs.get(bin.storageLocationId) ?? 0,
                            )}
                          />
                        </Flex>
                        <RedoButton
                          aria-label="Remove"
                          hierarchy="tertiary"
                          IconLeading={XClose}
                          onClick={() =>
                            removeBin(item.productId, bin.storageLocationId)
                          }
                          size="xs"
                        />
                      </Flex>
                    ))}
                  {(() => {
                    const hiddenBins = item.bins.filter(
                      (bin) => !productVisible.has(bin.storageLocationId),
                    );
                    if (hiddenBins.length === 0) {
                      return null;
                    }
                    const options: RedoListItem<string>[] = hiddenBins.map(
                      (bin) => {
                        if (!bin.lotNumber) {
                          return {
                            id: bin.storageLocationId,
                            value: bin.storageLocationId,
                            type: "text" as const,
                            text: bin.binName,
                            supportingText: `Available: ${bin.availableQuantity}`,
                          };
                        }
                        return {
                          id: bin.storageLocationId,
                          value: bin.storageLocationId,
                          type: "custom" as const,
                          useWrapper: true,
                          render: (
                            <Flex align="center" dir="row" gap="sm" w="full">
                              <Flex dir="column" grow={1}>
                                <Flex align="center" dir="row" gap="xs">
                                  <Text fontSize="sm">{bin.binName}</Text>
                                  <LotBadge
                                    expirationDate={bin.expirationDate}
                                    lotNumber={bin.lotNumber}
                                    size="xs"
                                  />
                                </Flex>
                                <Text fontSize="xxs" textColor="secondary">
                                  Available: {bin.availableQuantity}
                                </Text>
                              </Flex>
                            </Flex>
                          ),
                        };
                      },
                    );
                    return (
                      <RedoSingleSelectDropdownInput
                        options={options}
                        optionSelected={({ item: selected }) => {
                          addBin(item.productId, selected.value);
                        }}
                        placeholder="Add bin..."
                        size="xs"
                      />
                    );
                  })()}
                </>
              )}
            </Flex>
          );
        })}

        <Flex align="center" dir="row" gap="md" justify="flex-end" pt="lg">
          {onBypass && (
            <RedoButton
              hierarchy="tertiary"
              onClick={onBypass}
              size="md"
              text={bypassLabel}
              theme="normal"
            />
          )}
          <RedoButton
            hierarchy="secondary"
            onClick={onCancel}
            size="md"
            text="Cancel"
            theme="normal"
          />
          <RedoButton
            disabled={!isValid || submitLoad.pending}
            hierarchy="primary"
            onClick={() => triggerSubmit()}
            pending={submitLoad.pending}
            size="md"
            text={confirmLabel}
            theme="normal"
          />
        </Flex>
      </Flex>
    </RedoModal>
  );
});

export const InventoryAdjustmentWarningModal = memo(
  function InventoryAdjustmentWarningModal({
    open,
    lineItems,
    onConfirm,
    onCancel,
    onBypass,
    bypassLabel = "Bypass",
    confirmLabel = "Continue with inventory adjustment",
  }: InventoryAdjustmentWarningModalProps) {
    if (!lineItems.length) {
      return null;
    }

    return (
      <RedoModal
        hideFooter={!!onBypass}
        isOpen={open}
        modalSize="sm"
        onModalCloseRequested={onCancel}
        primaryButton={
          onBypass ? undefined : { text: confirmLabel, onClick: onConfirm }
        }
        secondaryButton={
          onBypass ? undefined : { text: "Cancel", onClick: onCancel }
        }
        theme="warn"
        title="No stock found in storage locations"
        TitleIcon={AlertTriangleIcon}
      >
        <Flex dir="column" gap="md" pb="lg">
          <Text>
            We could not find stock for these items in any media or storage
            location. Continuing will fulfill this shipment with an inventory
            adjustment instead of a warehouse transfer.
          </Text>
          <Flex dir="column" gap="xs">
            {lineItems.map((item) => (
              <Text fontSize="xs" key={item.productId} textColor="secondary">
                {item.productName}: {item.requiredQuantity}
              </Text>
            ))}
          </Flex>
          {onBypass && (
            <Flex align="center" dir="row" gap="md" justify="flex-end" pt="lg">
              {onBypass && (
                <RedoButton
                  hierarchy="tertiary"
                  onClick={onBypass}
                  size="md"
                  text={bypassLabel}
                  theme="normal"
                />
              )}
              <RedoButton
                hierarchy="secondary"
                onClick={onCancel}
                size="md"
                text="Cancel"
                theme="normal"
              />
              <RedoButton
                hierarchy="primary"
                onClick={onConfirm}
                size="md"
                text={confirmLabel}
                theme="normal"
              />
            </Flex>
          )}
        </Flex>
      </RedoModal>
    );
  },
);
